import { useState } from 'react';
import {
  useFetchServerInstanceMutation,
  useUpdateInstanceMutation,
  useCreateEnvironmentMutation
} from '../store/services/cloudlab/cloudlab';
import { useDispatch } from 'react-redux';
import { showNotification } from '../store/slices/NotificationSystemSlice';

export const useEnvironmentManagement = (onEnvironmentCreated) => {
  const [loadingCards, setLoadingCards] = useState([]);

  const [fetchServerInstance] = useFetchServerInstanceMutation();
  const [updateInstance] = useUpdateInstanceMutation();
  const [createEnvironment] = useCreateEnvironmentMutation();

  const dispatch = useDispatch();

  const pollEnvironmentStatus = async (envName) => {
    const maxRetries = 10;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const data = await fetchEnvironment(envName);
        if (data?.data?.envStatus === 'STARTED') {
          return data;
        }
        if (attempts < maxRetries) {
          return new Promise((resolve) => setTimeout(resolve, 10000)).then(poll);
        } else {
          throw new Error('Max retries reached. Environment not started.');
        }
      } catch (error) {
        console.error('Error in polling:', error);
        throw error;
      }
    };
    return poll();
  };

  const fetchEnvironment = async (envName) => {
    try {
      const response = await fetchServerInstance(envName).unwrap();
      return response;
    } catch (error) {
      console.error('Error fetching environment:', error);
      throw error;
    }
  };

  const updateEnvironment = async (envName) => {
    try {
      const response = await updateInstance(envName).unwrap();
      if (response?.message === 'success') {
        return response?.data;
      }
    } catch (error) {
      console.error('Error in UpdateEnvironment:', error);
      throw error;
    }
  };

  const handleCreateEnvironment = async (templateData) => {
    if (!templateData._id || typeof templateData._id !== 'string' || templateData._id.trim() === '') {
      dispatch(showNotification({
        message: 'Failed to create environment: templateId is missing or invalid.',
        success: false,
        duration: 3000,
        position: 'top-center',
      }));
      return;
    }
    try {
      const response = await createEnvironment({
        templateId: templateData._id,
        inputs: templateData.inputs,
      }).unwrap();

      if (response?.data?.envName && response?.message === 'success') {
        // Notify about new environment creation
        onEnvironmentCreated?.(templateData.templateName);
        dispatch(showNotification({
          message: `${templateData.templateName} environment created. Starting up...`,
          success: true,
          duration: 3000,
          position: 'top-center',
        }));
        const result = await pollEnvironmentStatus(response?.data?.envName);

        if (result?.data?.envName) {
          await updateEnvironment(result?.data?.envName);
        } else {
          console.error('No environment name in result, skipping UpdateEnvironment');
        }
        return result;
      } else {
        dispatch(showNotification({
          message: `Failed to create ${templateData.templateName}.`,
          success: false,
          duration: 3000,
          position: 'top-center',
        }));
        console.error('Environment creation response did not meet success criteria:', response);
      }
    } catch (error) {
      if (error.data?.message === 'license limit exceeded') {
        dispatch(showNotification({
          message: 'License limit exceeded. Please contact support or upgrade your plan.',
          success: false,
          duration: 4000,
          position: 'top-center',
        }));
        return;
      }
      if (error.response?.status === 402) {
        const { requiredCredits, currentCredits } = error.response.data;
        const displayCredits = currentCredits < 0 ? 0 : currentCredits;
        dispatch(showNotification({
          message: `Need ${requiredCredits} credits (you have ${displayCredits}). Add more to continue.`,
          success: false,
          duration: 4000,
          position: 'top-center',
        }));
      } else {
        dispatch(showNotification({
          message: `Failed to create ${templateData.templateName}.`,
          success: false,
          duration: 3000,
          position: 'top-center',
        }));
        console.error('Error in handleCreateEnvironment:', error);
        throw error;
      }
    }
  };

  const createEnvWithInputs = async (payload) => {
    setLoadingCards((prev) => [...prev, payload.templateName]);
    try {
      const result = await handleCreateEnvironment(payload);

      if (result?.message === 'license limit exceeded') {
        dispatch(showNotification({
          message: 'License limit exceeded. Please contact support or upgrade your plan.',
          success: false,
          duration: 4000,
          position: 'top-center',
        }));
        return;
      }
      const envStatus = result?.data?.envStatus;

      if (result?.data?.envName && result?.message === 'success') {
        dispatch(showNotification({
          message: `${payload.templateName} environment created. Starting up...`,
          success: true,
          duration: 3000,
          position: 'top-center',
        }));
      }

      if (envStatus === 'STARTED') {
        dispatch(showNotification({
          message: `${payload.templateName} environment created successfully!`,
          success: true,
          duration: 3000,
          position: 'top-center',
        }));
      } else if (envStatus === 'FAILED') {
        dispatch(showNotification({
          message: `${payload.templateName} environment failed to start.`,
          success: false,
          duration: 3000,
          position: 'top-center',
        }));
      } else {
        dispatch(showNotification({
          message: `${payload.templateName} environment status: ${envStatus || 'Unknown'}`,
          success: false,
          duration: 3000,
          position: 'top-center',
        }));
      }

      // After environment is created and started, refresh the instance list
      if (envStatus === 'STARTED' && fetchInstances) {
        fetchInstances();
      }
    } catch (error) {
      dispatch(showNotification({
        message: `Failed to create ${payload.templateName}.`,
        success: false,
        duration: 3000,
        position: 'top-center',
      }));
    } finally {
      setLoadingCards((prev) => prev.filter((t) => t !== payload.templateName));
    }
  };

  return {
    loadingCards,
    createEnvWithInputs,
    handleCreateEnvironment
  };
}; 